import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { OperationDate } from "../../pages/ActivityPage/ui/ActivityTable/types/types";
import {
  OrderType,
  SettledPeriod,
  State,
  Currency,
} from "../../pages/ActivityPage/ui/OrdersTable/types/types";

export interface OrdersFilters {
  date: {
    changePending: boolean;
    applied: OperationDate | null;
    notApplied: OperationDate | null;
    rangeApplied: (string | undefined)[];
    rangeNotApplied: (string | undefined)[];
  };
  operation: {
    changePending: boolean;
    applied: "ALL" | OrderType[] | null;
    notApplied: "ALL" | OrderType[] | null;
  };
  settledPeriod: {
    changePending: boolean;
    applied: "ALL" | SettledPeriod[] | null;
    notApplied: "ALL" | SettledPeriod[] | null;
  };
  state: {
    changePending: boolean;
    applied: "ALL" | State[] | null;
    notApplied: "ALL" | State[] | null;
  };
  currency: {
    changePending: boolean;
    applied: "ALL" | Currency[] | null;
    notApplied: "ALL" | Currency[] | null;
  };
}

interface InitialState {
  openModal: boolean;
  filterSelected:
    | "date"
    | "operation"
    | "settledPeriod"
    | "state"
    | "currency"
    | null;
  filters: OrdersFilters;
}

type FilterValue<K extends keyof OrdersFilters> = K extends "settledPeriod"
  ? OrdersFilters["settledPeriod"]["applied"]
  : K extends "state"
  ? OrdersFilters["state"]["applied"]
  : K extends "operation"
  ? OrdersFilters["operation"]["applied"]
  : K extends "date"
  ? OrdersFilters["date"]["applied"]
  : K extends "currency"
  ? OrdersFilters["currency"]["applied"]
  : never;

const initialState: InitialState = {
  openModal: false,
  filterSelected: null,
  filters: {
    date: {
      changePending: false,
      applied: "0",
      notApplied: "0",
      rangeApplied: [undefined, undefined],
      rangeNotApplied: [undefined, undefined],
    },
    operation: {
      changePending: false,
      applied: "ALL",
      notApplied: "ALL",
    },
    settledPeriod: {
      changePending: false,
      applied: "ALL",
      notApplied: "ALL",
    },
    state: {
      changePending: false,
      applied: "ALL",
      notApplied: "ALL",
    },
    currency: {
      changePending: false,
      applied: "ALL",
      notApplied: "ALL",
    },
  },
};

const ordersFiltersSlice = createSlice({
  name: "ordersFiltersSlice",
  initialState,
  reducers: {
    setOpenModal(
      state,
      action: PayloadAction<{
        openModal: boolean;
        filterSelected: InitialState["filterSelected"];
      }>
    ) {
      state.openModal = action.payload.openModal;
      state.filterSelected = action.payload.filterSelected;
    },
    setFilterSelected(
      state,
      action: PayloadAction<InitialState["filterSelected"]>
    ) {
      state.filterSelected = action.payload;
    },
    setFilterApplied<K extends keyof OrdersFilters>(
      state: InitialState,
      action: PayloadAction<{ filter: K; value: FilterValue<K> }>
    ) {
      state.filters[action.payload.filter].applied = action.payload.value;
    },
    setFilterNotApplied<K extends keyof OrdersFilters>(
      state: InitialState,
      action: PayloadAction<{ filter: K; value: FilterValue<K> }>
    ) {
      state.filters[action.payload.filter].notApplied = action.payload.value;
    },
    setFilterDateRangeNotApplied(
      state,
      action: PayloadAction<{ value: (string | undefined)[] }>
    ) {
      state.filters["date"].rangeNotApplied = action.payload.value;
    },
    setFilterDateRangeApplied(
      state,
      action: PayloadAction<{ value: (string | undefined)[] }>
    ) {
      state.filters["date"].rangeApplied = action.payload.value;
    },
    setFilterChangePending(
      state,
      action: PayloadAction<{ filter: keyof OrdersFilters; value: boolean }>
    ) {
      state.filters[action.payload.filter].changePending = action.payload.value;
    },
    resetDateFilter(state) {
      state.filters["date"] = initialState.filters["date"];
    },
    resetAllFilters(state) {
      state.filters = initialState.filters;
    },
    applyChanges(state) {
      (Object.keys(state.filters) as (keyof OrdersFilters)[]).forEach((filterKey) => {
        const filter = state.filters[filterKey];

        if (filterKey === "date") {
          state.filters.date.applied = state.filters.date.notApplied;
          state.filters.date.rangeApplied = [
            ...state.filters.date.rangeNotApplied,
          ];
        } else {
          state.filters[filterKey].applied =
            state.filters[filterKey].notApplied;
        }

        filter.changePending = false;
      });
    },
  },
});

export const {
  applyChanges,
  resetDateFilter,
  resetAllFilters,
  setFilterApplied,
  setFilterChangePending,
  setFilterDateRangeApplied,
  setFilterDateRangeNotApplied,
  setFilterNotApplied,
  setFilterSelected,
  setOpenModal,
} = ordersFiltersSlice.actions;

export default ordersFiltersSlice.reducer;
